import {ListView} from '@github-ui/list-view'
import {ListItem} from '@github-ui/list-view/ListItem'
import {ListItemDescription} from '@github-ui/list-view/ListItemDescription'
import {ListItemLeadingContent} from '@github-ui/list-view/ListItemLeadingContent'
import {ListItemLeadingVisual} from '@github-ui/list-view/ListItemLeadingVisual'
import {ListItemMainContent} from '@github-ui/list-view/ListItemMainContent'
import {ListItemTitle} from '@github-ui/list-view/ListItemTitle'
import {codeScanningAlertPath} from '@github-ui/paths'
import {Banner} from '@primer/react'

import {LABELS} from '../../../constants/labels'
import {SecurityAlertIcon} from './SecurityAlertIcon'

export type SecurityAlertNode = {
  readonly number: number | null | undefined
  readonly repository:
    | {
        readonly nameWithOwner: string
      }
    | null
    | undefined
  readonly status: string | null | undefined
  readonly title: string | null | undefined
}

export type RelationshipsSecurityAlertsListViewProps = {
  securityAlerts: ReadonlyArray<SecurityAlertNode | null | undefined> | null | undefined
}

export function RelationshipsSecurityAlertsListView({securityAlerts}: RelationshipsSecurityAlertsListViewProps) {
  const emptyBanner = (
    <Banner variant="info">
      <Banner.Title>{LABELS.emptySecurityAlertsList}</Banner.Title>
    </Banner>
  )

  const nodes = securityAlerts?.filter((node): node is SecurityAlertNode => !!node)
  if (!nodes) return emptyBanner

  const alerts = nodes
    .map(node => {
      const repoFullName = node.repository?.nameWithOwner
      if (!repoFullName || !node.number || !node.title) return null
      const [owner, repo] = repoFullName.split('/')
      if (!owner || !repo) return null

      return {
        number: node.number,
        owner,
        repo,
        status: node.status || 'OPEN',
        title: node.title,
      }
    })
    .filter((alert): alert is NonNullable<typeof alert> => !!alert)

  if (alerts.length === 0) return emptyBanner

  return (
    <ListView title={LABELS.relationNames.securityAlerts} variant="compact">
      {alerts.map(alert => {
        return (
          <ListItem
            key={`${alert.owner}/${alert.repo}-${alert.number}`}
            title={
              <ListItemTitle
                value={alert.title}
                href={codeScanningAlertPath({
                  owner: alert.owner,
                  repo: alert.repo,
                  alertNumber: alert.number,
                })}
                anchorClassName="markdown-title"
              />
            }
          >
            <ListItemLeadingContent>
              <ListItemLeadingVisual>
                <SecurityAlertIcon status={alert.status} />
              </ListItemLeadingVisual>
            </ListItemLeadingContent>

            <ListItemMainContent>
              <ListItemDescription>{`#${alert.number}`}</ListItemDescription>
            </ListItemMainContent>
          </ListItem>
        )
      })}
    </ListView>
  )
}
